import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

interface SecuritySection {
  title: string;
  body: string;
  points?: string[];
}

interface SecurityAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function SecurityPage() {
  const { t } = useTranslation('marketing');
  const sections = t('pages.security.sections', { returnObjects: true }) as SecuritySection[];
  const sidebarPoints = t('pages.security.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.security.cta.actions', { returnObjects: true }) as SecurityAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.security.meta.title')}
      metaDescription={t('pages.security.meta.description')}
      canonicalPath="/security"
      eyebrow={t('pages.security.hero.eyebrow')}
      title={t('pages.security.hero.title')}
      intro={t('pages.security.hero.intro')}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.security.sidebar.title')}</h2>
            <p>{t('pages.security.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.security.concerns.title')}</h2>
            <p>{t('pages.security.concerns.body')}</p>
            <div className="acc-public-page-actions">
              <PublicCtaLink href="/contact" variant="secondary">
                {t('pages.security.concerns.action')}
              </PublicCtaLink>
            </div>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.security.intro.title')}</h2>
        <p>{t('pages.security.intro.body')}</p>
        <p>{t('pages.security.intro.priority')}</p>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.security.disclaimer.title')}</h2>
        <p>{t('pages.security.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="security-cta-title">
        <div>
          <h2 id="security-cta-title">{t('pages.security.cta.title')}</h2>
          <p>{t('pages.security.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
